import { Injectable, inject, signal } from '@angular/core';
import {
  Storage,
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from '@angular/fire/storage';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EditorPresetsService } from '@principle-theorem/ng-interactions';
import { PdfService } from '@principle-theorem/ng-principle-shared';
import {
  IPatientFileSelectionDialogData,
  PatientFileSelectionDialogComponent,
} from '@principle-theorem/ng-media-manager';
import {
  BasicDialogService,
  DialogPresets,
  FileManagerService,
  IUploadStartedEvent,
  confirmationDialogData,
} from '@principle-theorem/ng-shared';
import {
  Media,
  Patient,
  Referral,
  ReferralMedia,
} from '@principle-theorem/principle-core';
import {
  IMedia,
  IPatient,
  IReferral,
  IReferralMedia,
  IPDFContent,
  ReferralActor,
  StafferActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  INamedDocument,
  WithRef,
  asyncForEach,
  isSameRef,
} from '@principle-theorem/shared';
import { ReferralService } from './referral.service';

@Injectable({
  providedIn: 'root',
})
export class ReferralMediaService {
  private _referralService = inject(ReferralService);
  private _snackBar = inject(MatSnackBar);
  private _storage = inject(Storage);
  private _dialog = inject(MatDialog);
  private _basicDialog = inject(BasicDialogService);
  private _fileDownload = inject(FileManagerService);
  private _pdfService = inject(PdfService);
  private _editorPresets = inject(EditorPresetsService);
  uploadInProgress = signal(false);

  async add(
    event: IUploadStartedEvent,
    referral: WithRef<IReferral>,
    actor: ReferralActor
  ): Promise<void> {
    this.uploadInProgress.set(true);
    const saveStartTime = this._referralService.startSaving();

    try {
      await event.task;

      await Firestore.addDoc(
        ReferralMedia.col(referral),
        ReferralMedia.init({
          actor: actor,
          name: event.file.name,
          path: event.storagePath,
        })
      );

      await this._referralService.updateLastActivityBy(referral, actor);
    } catch {
      this._snackBar.open('Error creating referral media');
    } finally {
      this.uploadInProgress.set(false);
      await this._referralService.stopSaving(saveStartTime);
    }
  }

  async uploadPdf(
    content: IPDFContent,
    fileName: string,
    referral: WithRef<IReferral>,
    actor: ReferralActor
  ): Promise<void> {
    this.uploadInProgress.set(true);
    const saveStartTime = this._referralService.startSaving();

    try {
      const extensions = this._editorPresets.defaultToHTMLExtensions();
      const blob = await this._pdfService.generateBlob(content, extensions);
      const uploadPath = Referral.storagePath(referral);
      const storagePath = this.generateMediaPath(uploadPath, fileName);

      const uploadTask = uploadBytesResumable(
        ref(this._storage, storagePath),
        blob,
        { contentType: 'application/pdf' }
      );

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          undefined,
          (error) => reject(error),
          () => resolve()
        );
      });

      await Firestore.addDoc(
        ReferralMedia.col(referral),
        ReferralMedia.init({
          actor: actor,
          name: fileName,
          path: storagePath,
        })
      );

      await this._referralService.updateLastActivityBy(referral, actor);

      this._snackBar.open('PDF attachment added successfully');
    } catch (error) {
      this._snackBar.open('Error uploading PDF attachment');
      throw error;
    } finally {
      this.uploadInProgress.set(false);
      await this._referralService.stopSaving(saveStartTime);
    }
  }

  async delete(
    selected: WithRef<IReferralMedia>[],
    referral: WithRef<IReferral>,
    lastActivityBy: ReferralActor
  ): Promise<void> {
    if (!selected || selected.length === 0) {
      return;
    }
    const saveStartTime = this._referralService.startSaving();

    try {
      await asyncForEach(selected, async (media) => {
        await Firestore.deleteDoc(media.ref);
      });

      await this._referralService.updateLastActivityBy(
        referral,
        lastActivityBy
      );
    } catch {
      this._snackBar.open('Error deleting referral media');
    } finally {
      await this._referralService.stopSaving(saveStartTime);
    }
  }

  async selectPatientFiles(
    patient: INamedDocument<IPatient>,
    referral: WithRef<IReferral>,
    actor: StafferActor,
    media: WithRef<IReferralMedia>[] = []
  ): Promise<void> {
    const patientDoc = await Firestore.getDoc(patient.ref);

    const selected = await this._dialog
      .open<
        PatientFileSelectionDialogComponent,
        IPatientFileSelectionDialogData,
        WithRef<IMedia>[]
      >(
        PatientFileSelectionDialogComponent,
        DialogPresets.extraLarge({
          data: { patient: patientDoc, selectedMedia: media },
        })
      )
      .afterClosed()
      .toPromise();

    if (!selected) {
      return;
    }

    const excludeExistingReferralFiles = selected.filter(
      (mediaFile) =>
        !media.some((item) => isSameRef(item.patientMediaRef, mediaFile))
    );

    if (!excludeExistingReferralFiles.length) {
      this._snackBar.open('The selected media already exists in the referral.');
      return;
    }

    await this.copyPatientMediaToReferral(
      referral,
      excludeExistingReferralFiles,
      actor
    );
  }

  async copyPatientMediaToReferral(
    referral: WithRef<IReferral>,
    patientMedia: WithRef<IMedia>[],
    actor: StafferActor
  ): Promise<void> {
    this.uploadInProgress.set(true);
    const saveStartTime = this._referralService.startSaving();

    try {
      const uploadPath = Referral.storagePath(referral);

      await asyncForEach(patientMedia, async (media) => {
        const newPath = this.generateMediaPath(uploadPath, media.name);

        await this.downloadAndUploadMedia(media.path, newPath);

        await Firestore.addDoc(
          ReferralMedia.col(referral),
          ReferralMedia.init({
            ...media,
            actor,
            patientMediaRef: media.ref,
            path: newPath,
            tags: [],
          })
        );
      });

      await this._referralService.updateLastActivityBy(referral, actor);
    } catch {
      this._snackBar.open('Error adding patient media to referral');
    } finally {
      this.uploadInProgress.set(false);
      await this._referralService.stopSaving(saveStartTime);
    }
  }

  async copyReferralMediaToPatient(
    patient: INamedDocument<IPatient>,
    referralMedia: WithRef<IReferralMedia>[]
  ): Promise<void> {
    this.uploadInProgress.set(true);
    const saveStartTime = this._referralService.startSaving();

    try {
      const uploadPath = Patient.storagePath(patient);

      await asyncForEach(referralMedia, async (media) => {
        const newPath = this.generateMediaPath(uploadPath, media.name);

        await this.downloadAndUploadMedia(media.path, newPath);

        await Firestore.addDoc(
          Patient.mediaCol(patient),
          Media.init({
            ...media,
            path: newPath,
          })
        );
      });
    } catch {
      this._snackBar.open('Error copying referral media to patient');
    } finally {
      this.uploadInProgress.set(false);
      await this._referralService.stopSaving(saveStartTime);
    }
  }

  async downloadMedia(media: WithRef<IReferralMedia>[]): Promise<void> {
    await this._fileDownload.downloadFiles('files', media);
  }

  async deleteMediaWithConfirmation(
    media: WithRef<IReferralMedia>[],
    referral: WithRef<IReferral>,
    currentUser: ReferralActor
  ): Promise<boolean> {
    const confirmed = await this._basicDialog.confirm(
      confirmationDialogData({
        title: 'Delete Media',
        prompt: `Are you sure you want to delete ${
          media.length > 1 ? `these ${media.length} items` : 'this item'
        }? This action cannot be undone.`,
        submitLabel: 'Delete',
        submitColor: 'warn',
      })
    );

    if (!confirmed) {
      return false;
    }

    await this.delete(media, referral, currentUser);
    return true;
  }

  async copyMediaToPatientWithValidation(
    patient: INamedDocument<IPatient>,
    media: WithRef<IReferralMedia>[]
  ): Promise<void> {
    const excludeExistingPatientFiles = media.filter(
      (item) => !item.patientMediaRef
    );

    if (!excludeExistingPatientFiles.length) {
      this._snackBar.open(
        'The selected media already exists in patient files.'
      );
      return;
    }

    await this.copyReferralMediaToPatient(patient, excludeExistingPatientFiles);
  }

  private async downloadAndUploadMedia(
    sourcePath: string,
    destinationPath: string
  ): Promise<void> {
    const url = await getDownloadURL(ref(this._storage, sourcePath));
    const response = await fetch(url);
    const blob = await response.blob();

    const uploadTask = uploadBytesResumable(
      ref(this._storage, destinationPath),
      blob
    );

    return new Promise<void>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        undefined, // No progress updates are needed
        (error) => reject(error),
        () => resolve()
      );
    });
  }

  private generateMediaPath(basePath: string, fileName: string): string {
    const timestamp = Date.now();
    return `${basePath}/${timestamp}_${fileName}`;
  }
}
